import React, { useEffect, useState } from 'react'
import { DeviceSelectionProps } from '../../lib/type'
import DeviceTile from './DeviceTile'
import { Subtotal } from '../Subtotal/Subtotal'
import { Button } from '@corratech/pylot-ui'
import { useTranslation } from 'next-i18next'
import cn from 'classnames'
import ds from './DeviceTile.module.scss'
import { BundleItem, Maybe, BundleItemOption } from '@pylot-data/fwrdschema'
import { useUI } from '@corratech/pylot-ui/context'
import classNames from 'classnames'

const getUniqueItems = (arr: string[]) => [...new Set(arr)]

export const DeviceSelection = ({
    product,
    setStep,
    step,
    selectedOptions,
    setSelectedOptions,
    addOns,
    addProductHandler,
    isAddToCartInProgress,
    isFlavourExist,
    isStarterKit
}: DeviceSelectionProps): JSX.Element => {
    const { t } = useTranslation(['common', 'plp', 'pdp'])
    const { cartEditId } = useUI()
    const devices = product?.items?.filter(
        (item) => item?.ooka_product_option == 'device'
    )

    const flavours = product?.items?.filter(
        (item: BundleItem | null) => item?.title === 'Flavours'
    )
    const packCount = flavours?.length ?? 0
    const flavorCount = flavours?.reduce((acc, curr) => {
        curr?.options?.forEach(
            (opt: BundleItemOption | null) => (acc += opt?.quantity ?? 0)
        )
        return acc
    }, 0)

    const selectedDevices = devices
        ?.map((device) => {
            return device?.options?.find((option) =>
                selectedOptions?.includes(String(option?.uid))
            )
        })
        .filter((device) => device)
    const requiredDevices =
        devices?.filter((device) => {
            if (device?.required) {
                return true
            }
        }) ?? []

    const renderAccessories = product?.items?.filter(
        (item) => item?.ooka_product_option == 'accessories'
    )

    const selectedRequiredDevice =
        devices?.filter((device) => {
            if (
                device?.required &&
                device?.options?.find((option) =>
                    selectedOptions?.includes(option?.uid!)
                )
            ) {
                return true
            }
        }) ?? []
    const renderType = (
        devices: Maybe<BundleItem>[] | undefined,
        isDevice = true
    ) =>
        devices?.map((device, ind: number) => {
            return (
                <div
                    key={device?.sku}
                    className={
                        device?.options && device?.options?.length > 1
                            ? ds['accessories-two-columns']
                            : undefined
                    }
                >
                    {isDevice && devices.length > 1 ? (
                        <p className={(ds['device-title'], 'price-container')}>
                            {isDevice
                                ? t(`DEVICE ${ind + 1}`)
                                : t(device?.title!)}
                        </p>
                    ) : (
                        ''
                    )}
                    <div
                        className={cn(
                            isDevice
                                ? ds['device-list']
                                : ds['accessories-list']
                        )}
                        key={device?.sku}
                    >
                        {device?.options?.map((option) => (
                            <div
                                key={option?.uid}
                                className={
                                    isDevice
                                        ? ds['device-tile']
                                        : ds['accessories-tile']
                                }
                            >
                                <DeviceTile
                                    device={device}
                                    option={option}
                                    selectedOptions={selectedOptions}
                                    setSelectedOptions={setSelectedOptions}
                                    isDevice={isDevice}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )
        })
    const buttonLabel = (): string => {
        const isUpsellExists = !!product?.upsell_products?.length
        const label = isUpsellExists || isFlavourExist
        if (cartEditId) return t('Update & Continue')
        if (label) return t('CONTINUE')
        return t(isAddToCartInProgress ? 'Adding product' : 'Go to Checkout')
    }

    const isDisabled = () => {
        if (requiredDevices.length > 0) {
            return !(requiredDevices.length <= selectedRequiredDevice.length)
        } else if (requiredDevices && requiredDevices?.length > 0) {
            return isStarterKit
                ? !selectedDevices?.length
                : selectedRequiredDevice &&
                      selectedRequiredDevice.length < requiredDevices.length
        }

        return (
            !selectedDevices?.length ||
            (devices?.length &&
                selectedRequiredDevice.length < requiredDevices.length)
        )
    }

    useEffect(() => {
        if (isStarterKit) {
            const _uids: string[] = product?.items
                ?.map((item) => {
                    if (item?.ooka_product_option === 'pods') {
                        return item?.options?.[0]?.uid || ''
                    }
                    return ''
                })
                .filter(Boolean) as string[]

            setSelectedOptions?.((oldState) =>
                getUniqueItems([...oldState, ..._uids])
            )
        }
    }, [isStarterKit, product])

    return (
        <>
            <div className={ds['main-container']}>
                {devices && devices?.length > 0 && (
                    <div className={ds['device-list-container']}>
                        <div className={ds['tittle']}>
                            <p className={ds['build-your']}>
                                {t('SELECT YOUR COLOR')}
                            </p>
                        </div>
                        <div className={ds['device-container']}>
                            {renderType(devices)}
                        </div>
                    </div>
                )}
                {renderAccessories && renderAccessories?.length > 0 && (
                    <div className={ds['device-list-container']}>
                        <div className={ds['tittle']}>
                            <p
                                className={cn(
                                    ds['build-your'],
                                    ds['accessories-title']
                                )}
                            >
                                {t('YOUR BUNDLE INCLUDES')}
                            </p>
                        </div>
                        <div
                            className={cn([ds['device-container']], {
                                [ds['device-container-grid']]:
                                    renderAccessories?.length > 1
                            })}
                        >
                            {renderType(renderAccessories, false)}
                        </div>
                    </div>
                )}
                {isStarterKit ? (
                    <div className={ds['include-card-wrapper']}>
                        <p className={cn(ds['include-card-wrapper-title'])}>
                            {t('YOUR BUNDLE INCLUDES')}
                        </p>
                        <div className={ds['include-card']}>
                            <div
                                aria-hidden="true"
                                className={cn(ds['include-card-content'])}
                            >
                                <div>
                                    <img src="/pods.png" alt="" />
                                </div>
                                <div>
                                    <h6>{`${packCount} Variety Pack`}</h6>
                                    <p>
                                        {`A curated variety of ${packCount} packs (${
                                            packCount * 2
                                        }
                                    pods)`}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : null}
                <div className={ds['bottom-container']}>
                    <div className={ds['free-shipping']}>
                        <p>{t('FREE SHIPPING ON ALL KITS')}</p>
                    </div>
                    <div className={ds['pods-footer']}>
                        <p>
                            {t(
                                'Enjoy free shipping on all kits and get everything you need to get started with OOKA delivered right to your door'
                            )}
                        </p>
                    </div>
                </div>
            </div>
            <div className={ds['price-container']}>
                <div className={ds['price']}>
                    <Subtotal
                        product={product}
                        selectedOptions={selectedOptions}
                        addOns={addOns}
                    />
                </div>
                <Button
                    type="button"
                    className={classNames(ds['add-to-cart-button'], {
                        [ds['add-to-cart-button-wider']]: cartEditId
                    })}
                    loading={false}
                    disabled={Boolean(isDisabled())}
                    onClick={() => {
                        if (
                            setStep &&
                            typeof step == 'number' &&
                            !isDisabled()
                        ) {
                            if (
                                product?.upsell_products?.length ||
                                isFlavourExist
                            ) {
                                setStep(step + 1)
                            } else if (addProductHandler) {
                                addProductHandler(null)
                            }
                        }
                    }}
                >
                    {buttonLabel()}
                </Button>
            </div>
        </>
    )
}
